import { useEffect, useMemo, useState } from 'react';
import { useGetList } from 'react-admin';
import { DashboardResources } from '@src/types';

import { HISTORY_WINDOW_MS, POLL_INTERVAL_MS } from './constants';
import { sortQueues } from './queueDisplay';
import type { QueueMetrics, QueueSample } from './types';

const MAX_SAMPLES = HISTORY_WINDOW_MS / POLL_INTERVAL_MS;

export interface QueueHistoryState {
  /** Latest metrics per queue, in canonical display order. */
  queues: QueueMetrics[];
  /** Rolling window of polled snapshots, oldest first. */
  history: QueueSample[];
  /** Epoch ms of the last successful poll, or null before the first one. */
  lastUpdatedAt: number | null;
  /** Whether auto-refresh polling is active. */
  live: boolean;
  setLive: (live: boolean) => void;
  refetch: () => void;
  error: unknown;
  isPending: boolean;
}

/**
 * Polls the shopify-sync resource on an interval and accumulates a rolling
 * client-side history of queue depths. The status query only reports current
 * numbers, so trends exist only for as long as the page has been open —
 * that's inherent to the API, not a limitation of this hook.
 */
export const useQueueHistory = (): QueueHistoryState => {
  const [live, setLive] = useState(true);
  const [history, setHistory] = useState<QueueSample[]>([]);

  const { data, dataUpdatedAt, error, isPending, refetch } = useGetList<QueueMetrics>(
    DashboardResources.ShopifySync,
    {},
    {
      refetchInterval: live ? POLL_INTERVAL_MS : false,
      // Pausing has to mean paused: a focus refetch would move the board
      // while the UI still says "Paused".
      refetchOnWindowFocus: live,
    }
  );

  // Keyed on `dataUpdatedAt`, not on `data`. React Query's structural sharing
  // hands back the *previous* array when a poll returns a deep-equal payload,
  // so an identity-keyed effect would never fire for an idle or stalled queue
  // — exactly the states this page exists to show.
  useEffect(() => {
    if (!data || dataUpdatedAt === 0) return;
    setHistory((prev) => {
      if (prev[prev.length - 1]?.time === dataUpdatedAt) return prev;
      const sample: QueueSample = { time: dataUpdatedAt, pending: {} };
      data.forEach((queue) => {
        sample.pending[queue.name] = queue.error == null ? queue.messagesPending : null;
      });
      const next = [...prev, sample];
      return next.length > MAX_SAMPLES ? next.slice(next.length - MAX_SAMPLES) : next;
    });
  }, [data, dataUpdatedAt]);

  const queues = useMemo(() => sortQueues(data ?? []), [data]);

  return {
    queues,
    history,
    lastUpdatedAt: dataUpdatedAt > 0 ? dataUpdatedAt : null,
    live,
    setLive,
    refetch,
    error,
    isPending,
  };
};
